import { useEffect } from "react";
import type { FC } from "react";
import { Button } from "react-bootstrap";

import { useAppSelector, useAppDispatch } from "store/hooks";
import { getTopicAssignments, setTopicAssignments } from "store/slices/assignmentNavigation";

import { useLazyGetAssignmentsInEnvelopeQuery } from "api/generated";

import { translateEnvelopeId } from "utils/translateEnvelopeId";
import { processApiError } from "utils/processApiError";

import IconArrowLeft from "images/icons/arrow-left.svg";
import IconArrowRight from "images/icons/arrow-right.svg";

interface Props {
  userAssignmentId: string;
  themeId: string;
  envelopeId: string;
  onNavigate: (assignmentId: string) => void;
}

export const EnvelopeAssignmentsNavigation: FC<Props> = ({ userAssignmentId, envelopeId, themeId, onNavigate }) => {
  const assignmentNavigation = useAppSelector(getTopicAssignments(`${themeId}-${envelopeId}`));
  const dispatch = useAppDispatch();

  const [getAssignmentsList, { error: apiLazyGetListError }] = useLazyGetAssignmentsInEnvelopeQuery();

  useEffect(() => {
    if (apiLazyGetListError === undefined) return;

    processApiError(apiLazyGetListError);
  }, [apiLazyGetListError]);

  useEffect(() => {
    if (assignmentNavigation === undefined) {
      getAssignmentsList({
        themeId,
        envelopeId: translateEnvelopeId(envelopeId),
      })
        .then(response => {
          const idList = response.data?.userAssignmentsCached.items.map(assignment => assignment.id) ?? [];
          dispatch(setTopicAssignments({ topicId: `${themeId}-${envelopeId}`, assignments: idList }));
        })
        .catch(error => {
          console.error(error);
          dispatch(setTopicAssignments({ topicId: `${themeId}-${envelopeId}`, assignments: [] }));
        });
    }
  }, [assignmentNavigation]);

  const navigatePrevious = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index === 0) {
      onNavigate(assignmentNavigation[assignmentNavigation.length - 1]);
      return;
    }

    onNavigate(assignmentNavigation[index - 1]);
  };

  const navigateNext = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index >= assignmentNavigation.length - 1) {
      onNavigate(assignmentNavigation[0]);
      return;
    }

    onNavigate(assignmentNavigation[index + 1]);
  };

  if (assignmentNavigation === undefined) return null;

  return (
    <div className="assignment-navigation">
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigatePrevious}>
        <IconArrowLeft />
      </Button>
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigateNext}>
        <IconArrowRight />
      </Button>
    </div>
  );
};
